<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { pwaLinkPoll, pwaLinkStart } from '../api';

const emit = defineEmits<{
  (e: 'authenticated'): void;
}>();

const status = ref<'init' | 'waiting' | 'expired' | 'error'>('init');
const deeplink = ref<string | null>(null);
const error = ref<string | null>(null);
let pollInterval: ReturnType<typeof setInterval> | null = null;
let pollAttempts = 0;
const MAX_POLL_ATTEMPTS = 60; // 60 × 5 сек = 5 мин

async function startFlow() {
  status.value = 'init';
  error.value = null;
  try {
    const resp = await pwaLinkStart();
    deeplink.value = resp.deeplink;
    status.value = 'waiting';
    pollAttempts = 0;

    // Открыть deeplink в новой вкладке (или той же если PWA)
    window.location.href = resp.deeplink;

    // Стартуем polling
    pollInterval = setInterval(async () => {
      pollAttempts += 1;
      if (pollAttempts > MAX_POLL_ATTEMPTS) {
        clearPolling();
        status.value = 'expired';
        return;
      }
      try {
        const result = await pwaLinkPoll(resp.token);
        if (result === 'ok') {
          clearPolling();
          // session_jwt cookie должен быть установлен → перезагрузить чтобы
          // App.vue с новой cookie прошёл meByCookie() и показал интерфейс
          emit('authenticated');
        } else if (result === 'expired') {
          clearPolling();
          status.value = 'expired';
        }
        // pending — продолжаем ждать
      } catch (e) {
        // Сеть сбоит — продолжаем polling
        console.warn('poll failed', e);
      }
    }, 5000);
  } catch (e) {
    status.value = 'error';
    error.value = e instanceof Error ? e.message : String(e);
  }
}

function clearPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

onMounted(() => {
  // Не стартуем автоматически — кнопка «Войти через MAX»
});

onUnmounted(() => {
  clearPolling();
});
</script>

<template>
  <section class="min-h-screen flex flex-col items-center justify-center p-6 gap-6 bg-white">
    <div class="text-center max-w-sm">
      <div class="text-3xl mb-2">📋 Сканер+ договор</div>
      <p class="text-sm text-gray-600">Серво-Юг</p>
    </div>

    <div v-if="status === 'init'" class="flex flex-col gap-4 items-center w-full max-w-sm">
      <button
        type="button"
        class="w-full py-4 bg-green-700 text-white rounded-lg font-semibold text-lg active:bg-green-800"
        @click="startFlow"
      >
        Войти через MAX
      </button>
      <p class="text-xs text-gray-500 text-center">
        Для входа нужен аккаунт MAX, добавленный в список доступа Серво-Юг.<br>
        После клика откроется бот «Серво-Юг Номер договора» — нажмите «Запустить».
      </p>
    </div>

    <div v-else-if="status === 'waiting'" class="flex flex-col gap-4 items-center text-center max-w-sm">
      <div class="text-4xl animate-pulse">⏳</div>
      <p class="font-medium">Ждём подтверждения в MAX...</p>
      <p class="text-sm text-gray-600">
        Откройте бот «Серво-Юг Номер договора» и нажмите «Запустить».<br>
        Это окно сразу обновится после успешного входа.
      </p>
      <a
        v-if="deeplink"
        :href="deeplink"
        class="text-blue-600 text-sm underline"
      >Открыть MAX ещё раз</a>
    </div>

    <div v-else-if="status === 'expired'" class="flex flex-col gap-4 items-center text-center max-w-sm">
      <div class="text-4xl">⌛</div>
      <p class="font-medium">Ссылка истекла (5 минут)</p>
      <button
        type="button"
        class="px-4 py-2 bg-green-700 text-white rounded font-medium"
        @click="startFlow"
      >
        Попробовать снова
      </button>
    </div>

    <div v-else-if="status === 'error'" class="flex flex-col gap-4 items-center text-center max-w-sm">
      <div class="text-4xl">⚠</div>
      <p class="text-red-600 text-sm">Ошибка: {{ error }}</p>
      <button
        type="button"
        class="px-4 py-2 bg-gray-200 rounded text-sm"
        @click="startFlow"
      >
        Повторить
      </button>
    </div>
  </section>
</template>
